import {useState, useEffect, use} from 'react';
import './index.css';

// react-router components
import {Routes, Route, useLocation, Navigate} from 'react-router-dom';

// @mui material components
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';

// Material Dashboard 2 React example components
import Sidenav from 'examples/Sidenav';
import Configurator from 'examples/Configurator';

// Material Dashboard 2 React themes
import theme from 'assets/theme';

// Material Dashboard 2 React Dark Mode themes
import themeDark from 'assets/theme-dark';

// Material Dashboard 2 React routes
import routes from './routes.js';

// Material Dashboard 2 React contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from 'context';

// Images
import brandWhite from 'assets/images/logo-ct.png';
import brandDark from 'assets/images/logo-ct-dark.png';
import ProtectedRoute from 'ProtectedRoute';
import {useDispatch, useSelector} from 'react-redux';
import {fetchUser, fetchMyPermissions} from 'store/slices/authSlice';
import {filterRoutesByPermissions} from 'utils/permissions';
import DeactivatedOverlay from 'components/DeactivatedOverlay';

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);

  const {pathname} = useLocation();
  const reduxDispatch = useDispatch();
  const {isAuthenticated, permissions, hasFullAccess, isSystemAdmin} = useSelector(
    state => state.auth,
  );

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute('dir', direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Dispatch fetchUser when the app is loaded to check if the user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      reduxDispatch(fetchUser());
      reduxDispatch(fetchMyPermissions());
    }
  }, [isAuthenticated]);

  // Sidebar and routing both render from this permission-filtered list, so a
  // restricted role never sees a link or a route for a module it can't use.
  const visibleRoutes = filterRoutesByPermissions(routes, permissions, hasFullAccess, isSystemAdmin);

  const getRoutes = (allRoutes, parentKey) =>
    allRoutes.map(route => {
      if (route.collapse) {
        // Sub-routes (e.g. "Edit Page" under "Pages") belong to the same
        // module as their parent entry for permission purposes.
        return getRoutes(route.collapse, route.key || parentKey);
      }

      if (route.route) {
        const routeElement = route.protected ? (
          <ProtectedRoute routeKey={route.key || parentKey} action={route.action}>
            {route.component}
          </ProtectedRoute> // Wrap protected routes
        ) : (
          route.component
        );

        return (
          <Route
            exact
            path={route.route}
            element={routeElement}
            key={route.key}
          />
        );
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{cursor: 'pointer'}}
      onClick={handleConfiguratorOpen}>
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <DeactivatedOverlay />
      {layout === 'dashboard' && isAuthenticated &&(
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
            brandName="Desteliq Admin"
            routes={visibleRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === 'vr' && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
      </Routes>
    </ThemeProvider>
  );
}
